import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Combobox,
  ErrorState,
  LoadingSkeleton,
  SectionCard,
  TextInput,
} from "@a2z/ui";
import type { AdminAreaView, AdminGovernorateView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { AreaRow } from "../components/AreaRow";
import { AreaFormDialog } from "../components/AreaFormDialog";
import { useAreasQuery } from "../services/useAreasQuery";
import { useAreaGovernoratesQuery } from "../services/useAreaGovernoratesQuery";

type Props = {
  locale: string;
};

const ALL = "__all__";
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

function govName(gov: AdminGovernorateView, locale: string): string {
  return (locale === "ar" ? gov.nameAr : gov.nameEn ?? gov.nameAr) || gov.nameAr;
}

export function AreasPage({ locale }: Props) {
  const t = useTranslations();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [queryInput, setQueryInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [governorateFilter, setGovernorateFilter] = useState<string>(ALL);
  const [page, setPage] = useState(1);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [queryInput]);

  // A filter change can shrink the result set below the current page —
  // always land back on page 1 when the search term or governorate changes.
  // Adjusted during render (not an effect) per React's "adjusting state when
  // a prop changes" pattern, so it happens in the same render pass instead
  // of triggering an extra one.
  const [appliedFilters, setAppliedFilters] = useState({
    q: debouncedQuery,
    governorateId: governorateFilter,
  });
  if (
    appliedFilters.q !== debouncedQuery ||
    appliedFilters.governorateId !== governorateFilter
  ) {
    setAppliedFilters({ q: debouncedQuery, governorateId: governorateFilter });
    setPage(1);
  }

  const { data, isLoading, error, refetch } = useAreasQuery({
    governorateId: governorateFilter === ALL ? undefined : governorateFilter,
    q: debouncedQuery || undefined,
    page,
    limit: PAGE_SIZE,
  });
  const { data: governorates } = useAreaGovernoratesQuery();

  const govById = useMemo(() => {
    const map = new Map<string, AdminGovernorateView>();
    for (const gov of governorates ?? []) map.set(gov.id, gov);
    return map;
  }, [governorates]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Deleting the last row of the last page shrinks the result set under the
  // current page, which otherwise reads "Page 4 of 3" over an empty table
  // with both arrows disabled — a dead end. Adjust during render (React's
  // documented pattern) rather than in an effect, which this codebase lints
  // against. Guarded on `!isLoading`: mid-fetch `total` is 0, and clamping
  // then would throw the admin back to page 1 on every page change.
  if (!isLoading && page > totalPages) {
    setPage(totalPages);
  }
  const isFiltered = Boolean(debouncedQuery) || governorateFilter !== ALL;

  // Grouping only spans the current page — a governorate's rows can split
  // across a page boundary, which is expected with server-side pagination.
  const sections = useMemo(() => {
    const map = new Map<string, AdminAreaView[]>();
    for (const area of items) {
      const arr = map.get(area.governorateId) ?? [];
      arr.push(area);
      map.set(area.governorateId, arr);
    }
    return [...map.entries()]
      .map(([governorateId, groupItems]) => ({
        governorateId,
        title: govById.has(governorateId)
          ? govName(govById.get(governorateId)!, locale)
          : governorateId,
        items: groupItems,
      }))
      .sort((a, b) => a.title.localeCompare(b.title, locale));
  }, [items, govById, locale]);

  if (isLoading && !data) {
    return <LoadingSkeleton variant="list" rows={6} />;
  }
  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  const governorateOptions = [
    { value: ALL, label: t(keys.areas.allGovernorates) },
    ...(governorates ?? []).map((gov) => ({
      value: gov.id,
      label: govName(gov, locale),
    })),
  ];

  return (
    <div className="flex w-full flex-col gap-5 lg:px-6 lg:py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.areas.title)}
          </h1>
          <p className="mt-1 text-[13.5px] text-[color:var(--text-muted)]">
            {t(keys.areas.subtitle)}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => setIsAddOpen(true)}
          className="h-10 rounded-lg px-4 text-sm font-semibold"
        >
          {t(keys.areas.addAction)}
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-xs">
          <TextInput
            name="area-search"
            type="search"
            value={queryInput}
            placeholder={t(keys.areas.searchPlaceholder)}
            onChange={(e) => setQueryInput(e.target.value)}
          />
        </div>
        <div className="w-full sm:max-w-xs">
          <Combobox
            value={governorateFilter}
            onChange={(value) => setGovernorateFilter(value ?? ALL)}
            options={governorateOptions}
            aria-label={t(keys.areas.governorateLabel)}
          />
        </div>
      </div>

      <p className="text-[13px] text-[color:var(--text-muted)]">
        {t(keys.areas.resultsCount, { shown: items.length, total })}
      </p>

      {sections.length === 0 ? (
        <p className="py-10 text-center text-sm text-[color:var(--text-muted)]">
          {t(isFiltered ? keys.areas.noResultsFiltered : keys.areas.noResults)}
        </p>
      ) : (
        sections.map((section) => (
          <SectionCard
            key={section.governorateId}
            title={section.title}
            right={
              <span className="text-[13px] font-semibold text-[color:var(--text-muted)]">
                {section.items.length}
              </span>
            }
          >
            <ul className="flex flex-col gap-2">
              {section.items.map((area) => (
                <AreaRow key={area.id} area={area} />
              ))}
            </ul>
          </SectionCard>
        ))
      )}

      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 pt-1">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {t(keys.common.previous)}
          </Button>
          <span className="text-[13px] text-[color:var(--text-muted)]">
            {t(keys.areas.pageIndicator, { page, totalPages })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            {t(keys.common.next)}
          </Button>
        </div>
      ) : null}

      {isAddOpen ? (
        <AreaFormDialog isOpen onClose={() => setIsAddOpen(false)} />
      ) : null}
    </div>
  );
}
